import { useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/firebase/useAuth";
import type { MyLessonEntry } from "@/services/myLessonsService";

export function useMyLessons() {
  const { user } = useAuth();
  const [myLessons, setMyLessons] = useState<Map<string, MyLessonEntry>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setMyLessons(new Map());
      setLoading(false);
      return;
    }

    setLoading(true);
    const myLessonsRef = collection(db(), "users", user.uid, "myLessons");

    const unsubscribe = onSnapshot(
      myLessonsRef,
      (snapshot) => {
        const lessonsMap = new Map<string, MyLessonEntry>();
        snapshot.docs.forEach((doc) => {
          const data = doc.data() as MyLessonEntry;
          lessonsMap.set(doc.id, data);
        });
        if (import.meta.env.DEV) {
          console.log(`[useMyLessons] Snapshot update - ${lessonsMap.size} lessons`, 
            Array.from(lessonsMap.entries()).map(([id, l]) => ({ id, status: l.status, title: l.lessonTitle }))
          );
        }
        setMyLessons(lessonsMap);
        setLoading(false);
        setError(null);
      },
      (err) => {
        if (import.meta.env.DEV) {
          console.error("Error fetching my lessons:", err);
        }
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  return { myLessons, loading, error };
}
